import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, IconButton, Avatar, Chip, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import { Icon } from '@iconify/react';
import { useNavigate, useParams } from 'react-router-dom';
import { common, grey } from 'src/theme/palette';

const STATUS_CONFIG = {
    NEW: { color: '#00A76F', contactLabel: 'Contact Lead', followUpLabel: 'Follow up' },
    CONSULTATION_BOOKED: { color: '#1890FF', contactLabel: 'Record Attendance' },
    CONSULTATION_ATTENDED: { color: '#54D62C', contactLabel: 'Follow up' },
    TREATMENT_STARTED: { color: '#FFC107', contactLabel: 'Complete Treatment' },
    FAILED_TO_ATTEND_CONSULTATION: { color: '#FF4842', contactLabel: 'Follow up' },
    CLOSED: { color: '#637381', contactLabel: 'View' },
    DEFAULT: { color: grey[300], contactLabel: 'Contact Lead' },
};

const getStatusConfig = (status, contactedCount) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.DEFAULT;
    if (status === 'NEW' && contactedCount > 0) {
        return { ...config, contactLabel: config.followUpLabel || 'Follow up' };
    }
    return config;
};

const getRelativeTime = (dateStr) => {
    if (!dateStr) return 'No contact';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
};

const MobileLeadCard = ({ row, onContact, onMoreAction }) => {
    const navigate = useNavigate();
    const { practiceId } = useParams();
    const [menuAnchor, setMenuAnchor] = React.useState(null);

    const {
        id,
        /* eslint-disable camelcase */
        first_name: firstName,
        last_name: lastName,
        email,
        phone,
        treatment,
        treatment_colour: treatmentColour,
        status,
        contacted_count: contactedCount,
        last_contacted_date: lastContactedDate,
        /* eslint-enable camelcase */
    } = row;

    const fullName = `${firstName || ''} ${lastName || ''}`.trim() || 'Unknown';
    const initials = `${(firstName || '?')[0]}${(lastName || '?')[0]}`.toUpperCase();
    const statusConfig = getStatusConfig(status, contactedCount);

    const handleViewDetails = () => {
        navigate(`/dashboard/practice/${practiceId}/lead-detail/${id}`);
    };

    const handleContact = () => {
        if (onContact) onContact(row);
    };

    const handleMenuOpen = (e) => {
        e.stopPropagation();
        setMenuAnchor(e.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
    };

    const handleMenuAction = (action) => {
        handleMenuClose();
        if (onMoreAction) onMoreAction(row, action);
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                p: '12px 14px',
                borderLeft: `3px solid ${statusConfig.color}`,
                borderBottom: '1px solid rgba(145, 158, 171, 0.12)',
                bgcolor: '#fff',
                '&:active': { bgcolor: 'rgba(145, 158, 171, 0.08)' },
            }}
        >
            {/* Row 1: Avatar, Name, Phone/Email icons, Menu */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Avatar
                    sx={{
                        width: 36, height: 36, fontSize: '13px', fontWeight: 600,
                        bgcolor: statusConfig.color, color: common.white, flexShrink: 0,
                    }}
                >
                    {initials}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        sx={{
                            fontSize: '13px', fontWeight: 600, color: '#212B36',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}
                    >
                        {fullName}
                    </Typography>
                </Box>
                {phone && (
                    <IconButton
                        component="a"
                        href={`tel:${phone}`}
                        onClick={(e) => e.stopPropagation()}
                        sx={{ width: 44, height: 44, color: '#637381' }}
                    >
                        <Icon icon="mdi:phone-outline" width={18} />
                    </IconButton>
                )}
                {email && (
                    <IconButton
                        component="a"
                        href={`mailto:${email}`}
                        onClick={(e) => e.stopPropagation()}
                        sx={{ width: 44, height: 44, color: '#637381' }}
                    >
                        <Icon icon="mdi:email-outline" width={18} />
                    </IconButton>
                )}
                <IconButton onClick={handleMenuOpen} sx={{ width: 44, height: 44, color: '#637381' }}>
                    <Icon icon="mdi:dots-vertical" width={20} />
                </IconButton>
                <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleMenuClose}>
                    <MenuItem onClick={() => handleMenuAction('archive')}>
                        <ListItemIcon><Icon icon="mdi:archive-outline" width={18} /></ListItemIcon>
                        <ListItemText>Archive</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => handleMenuAction('edit')}>
                        <ListItemIcon><Icon icon="mdi:pencil-outline" width={18} /></ListItemIcon>
                        <ListItemText>Edit</ListItemText>
                    </MenuItem>
                </Menu>
            </Box>

            {/* Row 2: Treatment */}
            {treatment && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', pl: '46px' }}>
                    {treatmentColour && (
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: treatmentColour, flexShrink: 0 }} />
                    )}
                    <Typography sx={{ fontSize: '12px', color: '#637381' }}>
                        {treatment}
                    </Typography>
                </Box>
            )}

            {/* Row 3: Status + Last contacted */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', pl: '46px' }}>
                {contactedCount > 0 && (
                    <Chip
                        label={`Follow up ${contactedCount}`}
                        size="small"
                        sx={{
                            height: '22px', fontSize: '11px', fontWeight: 600,
                            bgcolor: 'rgba(0, 167, 111, 0.08)', color: '#007B55',
                        }}
                    />
                )}
                <Typography sx={{ fontSize: '12px', color: '#919EAB' }}>
                    {getRelativeTime(lastContactedDate)}
                </Typography>
            </Box>

            {/* Row 4: Action buttons */}
            <Box sx={{ display: 'flex', gap: '8px', pl: '46px', mt: '4px' }}>
                <Box
                    component="button"
                    onClick={handleContact}
                    sx={{
                        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        gap: '4px', py: '6px', px: '8px',
                        border: '1px solid', borderColor: statusConfig.color, borderRadius: '8px',
                        bgcolor: 'transparent', color: statusConfig.color,
                        fontSize: '11px', fontWeight: 600, cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        transition: 'all 0.15s',
                        '&:active': { bgcolor: `${statusConfig.color}14` },
                    }}
                >
                    <Icon icon="mdi:phone-outline" width={14} />
                    {statusConfig.contactLabel}
                </Box>
                <Box
                    component="button"
                    onClick={handleViewDetails}
                    sx={{
                        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        gap: '4px', py: '6px', px: '8px',
                        border: '1px solid rgba(145, 158, 171, 0.24)', borderRadius: '8px',
                        bgcolor: 'transparent', color: '#212B36',
                        fontSize: '11px', fontWeight: 600, cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        transition: 'all 0.15s',
                        '&:active': { bgcolor: 'rgba(145, 158, 171, 0.08)' },
                    }}
                >
                    Details
                    <Icon icon="mdi:chevron-right" width={14} />
                </Box>
            </Box>
        </Box>
    );
};

MobileLeadCard.propTypes = {
    row: PropTypes.object.isRequired,
    onContact: PropTypes.func,
    onMoreAction: PropTypes.func,
};

export default MobileLeadCard;
